import React, { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { formatDateTime } from '@/lib/dateFormat'
import { getSalesOrderHistoryApi, type SalesOrderHistory } from '@/services/salesOrders'
import type { ProductionOrder } from '@/services/productionOrders'

export interface ProductionOrderLineHistoryModalProps {
  isOpen: boolean
  row?: ProductionOrder | null
  order?: ProductionOrder | null
  onClose: () => void
}

export function ProductionOrderLineHistoryModal({
  isOpen,
  row,
  order,
  onClose,
}: ProductionOrderLineHistoryModalProps): React.ReactElement | null {
  const target = order || row
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<SalesOrderHistory[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !target?.sales_order_id) return

    let cancelled = false
    setLoading(true)
    setError(null)

    getSalesOrderHistoryApi(target.sales_order_id)
      .then((items) => {
        if (cancelled) return
        const lineLogs = items.filter(
          (item) => !item.line_id || item.line_id === target.sales_order_line_id,
        )
        setHistory(lineLogs.length > 0 ? lineLogs : items)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Không thể tải lịch sử dòng')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, target])

  if (!isOpen || !target) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3.5">
          <h3 className="font-bold text-slate-800 text-base">
            Lịch sử dòng hàng: <span className="font-mono text-emerald-800">{target.variant_code}</span> ({target.item_code})
          </h3>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-5">
          {target.source_changed && (
            <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <strong className="font-bold">Thay đổi từ Sale chưa xem:</strong>{' '}
              {target.source_changed_note || 'Đơn hàng đã được chỉnh sửa từ phía bán hàng.'}
            </div>
          )}

          {loading ? (
            <p className="py-8 text-center text-sm text-slate-400">Đang tải lịch sử...</p>
          ) : error ? (
            <p className="py-8 text-center text-sm text-rose-600">{error}</p>
          ) : history.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500 italic">
              Chưa có bản ghi lịch sử nào cho dòng này.
            </p>
          ) : (
            <div className="relative border-l-2 border-slate-200 ml-3 space-y-4">
              {history.map((log) => (
                <div key={log.id} className="relative pl-5">
                  <div className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-slate-400" />
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="font-bold text-slate-800">{log.action}</span>
                    <span className="text-slate-400 font-mono">{formatDateTime(log.at)}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-600">
                    Bởi: <strong className="text-slate-700">{log.by || 'Hệ thống'}</strong>
                  </div>
                  {log.reason && (
                    <div className="mt-1 text-xs text-slate-700 bg-slate-100 rounded px-2 py-1">
                      Lý do: <em>{log.reason}</em>
                    </div>
                  )}
                  {log.after && (
                    <pre className="mt-1 text-xs font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto">
                      {JSON.stringify(log.after, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}
